import { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Mail, ArrowRight, ArrowLeft, RefreshCw, Shield, Plane, Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_be106047-7bc1-4755-b814-a58a16500b58/artifacts/p2la4lzy_ChatGPT%20Image%20Dec%2028%2C%202025%2C%2009_16_43%20PM.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const navigate = useNavigate();
  const { requestOTP, verifyOTP, resendOTP, refreshUser } = useAuth();
  const [step, setStep] = useState("email"); // email | otp | password
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [resendTimer, setResendTimer] = useState(0);
  const [demoOtp, setDemoOtp] = useState(null);
  const otpRefs = useRef([]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      // Check which login method to use (staff -> password, admin/agent -> OTP)
      let method = "otp";
      try {
        const res = await axios.post(`${API}/auth/check-login-method`, { email });
        method = res.data?.method || "otp";
      } catch {
        // Fall back to OTP on check failure
      }

      if (method === "password") {
        setStep("password");
        setLoading(false);
        return;
      }

      const response = await requestOTP(email);
      if (response.demo_otp) {
        setDemoOtp(response.demo_otp);
      }
      toast.success("OTP sent to your email!");
      setStep("otp");
      setResendTimer(60);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to send OTP. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!password) { toast.error("Enter your password"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/password-login`, { email, password });
      const { token: newToken, user } = res.data;
      localStorage.setItem("token", newToken);
      sessionStorage.removeItem("token");
      if (refreshUser) await refreshUser();
      else window.location.href = (user.role === "admin" || user.role === "employee") ? "/admin" : "/agent";
      toast.success(`Welcome back, ${user.name}!`);
      if (user.role === "admin" || user.role === "employee") {
        navigate("/admin");
      } else {
        navigate("/agent");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOTPKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOTPPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newOtp = [...otp];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newOtp[i] = pastedData[i];
      }
      setOtp(newOtp);
      const nextEmptyIndex = newOtp.findIndex((digit) => !digit);
      otpRefs.current[nextEmptyIndex === -1 ? 5 : nextEmptyIndex]?.focus();
    }
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      toast.error("Please enter the complete 6-digit OTP");
      return;
    }

    setLoading(true);
    try {
      const user = await verifyOTP(email, otpString);
      toast.success(`Welcome back, ${user.name}!`);
      if (user.role === "admin" || user.role === "employee") {
        navigate("/admin");
      } else {
        navigate("/agent");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Invalid OTP. Please try again.";
      toast.error(message);
      setOtp(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0 || resending) return;
    setResending(true);
    try {
      const response = await resendOTP(email);
      if (response.demo_otp) {
        setDemoOtp(response.demo_otp);
      }
      toast.success("New OTP sent to your email!");
      setResendTimer(60);
      setOtp(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();
    } catch (error) {
      toast.error("Failed to resend OTP. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const goBackToEmail = () => {
    setStep("email");
    setOtp(["", "", "", "", "", ""]);
    setPassword("");
    setDemoOtp(null);
  };

  return (
    <div className="h-screen bg-gradient-to-br from-emerald-50 via-white to-amber-50 overflow-hidden" data-testid="login-page">
      <div className="h-full flex">
        {/* Left Panel - Branding */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-emerald-600 to-emerald-800 p-10 flex-col justify-between relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-64 h-64 border border-white/30 rounded-full" />
            <div className="absolute bottom-20 right-10 w-96 h-96 border border-white/20 rounded-full" />
            <div className="absolute top-1/2 left-1/3 w-48 h-48 border border-white/20 rounded-full" />
          </div>
          
          <div className="relative z-10">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Logo" className="h-12 w-12 rounded-lg" />
              <div>
                <h1 className="text-white text-xl font-bold">Happy Holidays DMC</h1>
                <p className="text-emerald-200 text-sm">B2B Travel Platform</p>
              </div>
            </div>
          </div>

          <div className="relative z-10 space-y-5">
            <h2 className="text-4xl font-bold text-white leading-tight">
              Your Gateway to<br />Premium Travel<br />Experiences
            </h2>
            <p className="text-emerald-100 text-lg max-w-md">
              Join 500+ travel agents who trust us for curated packages, competitive pricing, and exceptional service.
            </p>
            
            <div className="flex items-center gap-8 pt-2">
              <div>
                <p className="text-3xl font-bold text-white">500+</p>
                <p className="text-emerald-200 text-sm">Active Agents</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-white">100+</p>
                <p className="text-emerald-200 text-sm">Destinations</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-white">24/7</p>
                <p className="text-emerald-200 text-sm">Support</p>
              </div>
            </div>
          </div>

          <div className="relative z-10">
            <p className="text-emerald-200 text-sm">
              Secure • Reliable • Trusted Since 2020
            </p>
          </div>
        </div>

        {/* Right Panel - Login Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-10">
          <div className="w-full max-w-md">
            {/* Mobile Logo */}
            <div className="lg:hidden flex items-center justify-center gap-3 mb-6">
              <img src={LOGO_URL} alt="Logo" className="h-10 w-10 rounded-lg" />
              <div>
                <h1 className="text-lg font-bold text-gray-900">Happy Holidays DMC</h1>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-xl border border-gray-100 p-7"
            >
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Plane className="w-6 h-6 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Welcome Back</h2>
                <p className="text-gray-500 text-sm mt-1">
                  {step === "email" 
                    ? "Sign in with your registered email" 
                    : step === "password"
                    ? "Enter your staff password"
                    : "Enter the verification code"}
                </p>
              </div>

              <AnimatePresence mode="wait">
                {step === "email" ? (
                  <motion.form
                    key="email-form"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    onSubmit={handleEmailSubmit}
                    className="space-y-5"
                    data-testid="login-email-form"
                  >
                    <div>
                      <Label htmlFor="email" className="text-gray-700 font-medium">Email Address</Label>
                      <div className="relative mt-1.5">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input 
                          id="email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-10 h-11 border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
                          placeholder="you@company.com"
                          data-testid="login-email"
                          autoFocus
                        />
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      disabled={loading}
                      className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-xl"
                      data-testid="login-request-otp"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                          Sending OTP...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          Continue <ArrowRight className="w-4 h-4" />
                        </span>
                      )}
                    </Button>
                  </motion.form>
                ) : step === "password" ? (
                  <motion.form
                    key="password-form"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    onSubmit={handlePasswordSubmit}
                    className="space-y-5"
                    data-testid="login-password-form"
                  >
                    <button type="button" onClick={goBackToEmail} className="text-xs text-gray-500 hover:text-emerald-600 flex items-center gap-1" data-testid="password-back-btn">
                      <ArrowLeft className="w-3 h-3" /> Use a different email
                    </button>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800">
                      <strong>Staff login:</strong> {email}
                    </div>
                    <div>
                      <Label className="text-gray-700 font-medium">Password</Label>
                      <div className="relative mt-1.5">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          className="pl-10 pr-10 h-11 border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
                          placeholder="Enter your password"
                          data-testid="login-password"
                          autoFocus
                          autoComplete="current-password"
                        />
                        <button type="button" onClick={() => setShowPassword(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <Button type="submit" disabled={loading} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-xl" data-testid="login-password-submit">
                      {loading ? (
                        <span className="flex items-center justify-center gap-2"><span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> Signing in...</span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">Sign In <ArrowRight className="w-4 h-4" /></span>
                      )}
                    </Button>
                  </motion.form>
                ) : (
                  <motion.form
                    key="otp-form"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    onSubmit={handleOTPSubmit}
                    className="space-y-5"
                    data-testid="login-otp-form"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-gray-700 font-medium">Verification Code</Label>
                        <button
                          type="button"
                          onClick={goBackToEmail}
                          className="text-sm text-emerald-600 hover:text-emerald-700 flex items-center gap-1 font-medium"
                        >
                          <ArrowLeft className="w-3 h-3" /> Change
                        </button>
                      </div>
                      
                      <p className="text-sm text-gray-500 mb-3">
                        Code sent to <span className="font-medium text-gray-700">{email}</span>
                      </p>

                      {/* Demo Mode OTP Display */}
                      {demoOtp && (
                        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                          <p className="text-xs text-amber-700 font-medium mb-1">Demo Mode</p>
                          <p className="text-2xl font-bold text-amber-800 tracking-[0.3em] text-center">{demoOtp}</p>
                        </div>
                      )}

                      <div className="flex gap-2 justify-center" onPaste={handleOTPPaste}>
                        {otp.map((digit, index) => (
                          <Input
                            key={index}
                            ref={(el) => (otpRefs.current[index] = el)}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handleOTPChange(index, e.target.value)}
                            onKeyDown={(e) => handleOTPKeyDown(index, e)}
                            className="w-11 h-12 text-center text-xl font-bold border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 rounded-xl"
                            data-testid={`otp-input-${index}`}
                            autoFocus={index === 0}
                          />
                        ))}
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      disabled={loading || otp.join("").length !== 6}
                      className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-xl disabled:opacity-50"
                      data-testid="login-verify-otp"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                          Verifying...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <Shield className="w-4 h-4" /> Verify & Sign In
                        </span>
                      )}
                    </Button>

                    <div className="text-center">
                      <button
                        type="button"
                        onClick={handleResendOTP}
                        disabled={resendTimer > 0 || resending}
                        className={`text-sm flex items-center justify-center mx-auto gap-1 font-medium ${
                          resendTimer > 0 
                            ? "text-gray-400 cursor-not-allowed" 
                            : "text-emerald-600 hover:text-emerald-700"
                        }`}
                      >
                        <RefreshCw className={`w-3 h-3 ${resending ? "animate-spin" : ""}`} />
                        {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend Code"}
                      </button>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>

              <div className="mt-6 pt-5 border-t border-gray-100 text-center">
                <p className="text-gray-500 text-sm mb-3">
                  New to Happy Holidays DMC?
                </p>
                <Link to="/agent-register">
                  <Button variant="outline" className="w-full h-10 border-gray-200 hover:bg-gray-50 font-medium rounded-xl text-sm">
                    Register as Agent <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </motion.div>

            <p className="text-center text-gray-400 text-xs mt-4">
              Secure login powered by OTP verification
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
